import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import useEsTelefono from "../../../../utils/esTelefono";
import { abrirAulaEnPestana, mensajeDeAula } from "../../../../utils/abrirAula";
import { AulasEnVivoService } from "../../../../services/v2/aulasEnVivo.service";
import "./aulasEnVivo.css";

const servicio = new AulasEnVivoService();
const REFRESCO_MS = 30000;

/*
	Aulas En Vivo (Fase 2c, 2026-10-16): supervisión de las clases del aula virtual. Tres bloques: En Vivo (salas abiertas
	ahora), Sin Abrir (ya debió empezar y no hay sala o el maestro no ha entrado; en oro) y Más Tarde Hoy.
	Carga al abrir, botón Actualizar y refresco cada 30 s SOLO con la pestaña visible; nunca sondea fuera de esta pantalla.
	Al tocar una tarjeta En Vivo se ve quién está adentro (cámara y micrófono) y quién falta.
*/

type Persona = { name: string; video: boolean; mic: boolean };
type Clase = {
	scheduleId: number; course: string; period?: string | null; teacher: string | null; startsLabel: string | null; endsLabel: string | null;
	startsAtMs: number | null; enrolled: number;
};
type EnVivo = Clase & {
	openedLabel: string; connected: number; withVideo: number; withMic: number; teacherIn: boolean;
	people: Persona[]; others: Persona[]; missing: string[];
};
type SinAbrir = Clase & { waiting: number; roomOpen: boolean };
type Datos = { bbbOk: boolean; updatedAtMs: number; live: EnVivo[]; notOpened: SinAbrir[]; later: Clase[] };

// Hora de la Ciudad de México en am/pm (las horas de las clases son de allá, sin importar dónde esté quien mira).
const horaDe = (ms: number): string => {
	const p = new Intl.DateTimeFormat("en-US", { timeZone: "America/Mexico_City", hour: "numeric", minute: "2-digit", hour12: true }).formatToParts(new Date(ms));
	const v = (t: string) => p.find((x) => x.type === t)?.value ?? "";
	return `${v("hour")}:${v("minute")} ${v("dayPeriod").toLowerCase()}`;
};
const rango = (c: Clase) => c.startsLabel ? `${c.startsLabel} – ${c.endsLabel}` : "";

// Card heading: course, then period, then time · teacher (Esteban, 2026-10-20: the module name is not shown).
const Cabeza = ({ c, sinMaestro }: { c: Clase; sinMaestro?: boolean }) => (
	<React.Fragment>
		<div className="auCurso">{c.course}</div>
		{c.period ? <div className="auPeriodo">{c.period}</div> : null}
		<div className="auSub">{[rango(c), c.teacher ?? (sinMaestro ? "Sin maestro" : null)].filter(Boolean).join(" · ")}</div>
	</React.Fragment>
);

const Estado = ({ si }: { si: boolean }) => si ? <span className="auSi">Sí</span> : <span className="auNo">No</span>;

const AulasEnVivoController = () => {
	const movil = useEsTelefono();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusRef?.[legacyCampusKey];
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;

	const [datos, setDatos] = useState<Datos | null>(null);
	const [fallo, setFallo] = useState<string | null>(null);
	const [cargando, setCargando] = useState(false);
	const [abierta, setAbierta] = useState<number | null>(null);
	const [entrando, setEntrando] = useState<number | null>(null);
	const [errorEntrada, setErrorEntrada] = useState<{ id: number; texto: string } | null>(null);
	const ultima = useRef(0);

	const cargar = useCallback(() => {
		if (campusId === null) {
			return;
		}
		ultima.current = Date.now();
		setCargando(true);
		servicio.list(campusId)
			.then((r) => { setDatos(r.data); setFallo(null); })
			.catch((e) => setFallo(e?.response?.data?.message || "No pudimos cargar las aulas."))
			.finally(() => setCargando(false));
	}, [campusId]);

	useEffect(() => {
		cargar();
		// Solo con la pestaña visible: al volver a verla, se actualiza si pasaron más de 30 s.
		const cada = window.setInterval(() => { if (document.visibilityState === "visible") { cargar(); } }, REFRESCO_MS);
		const alVolver = () => { if (document.visibilityState === "visible" && Date.now() - ultima.current >= REFRESCO_MS) { cargar(); } };
		document.addEventListener("visibilitychange", alVolver);
		return () => { window.clearInterval(cada); document.removeEventListener("visibilitychange", alVolver); };
	}, [cargar]);

	const supervisar = async (id: number) => {
		if (campusId === null || entrando !== null) {
			return;
		}
		setEntrando(id);
		setErrorEntrada(null);
		try {
			await abrirAulaEnPestana(async () => (await servicio.supervise(campusId, id)).data.url);
		} catch (e) {
			setErrorEntrada({ id, texto: mensajeDeAula(e) });
		} finally {
			setEntrando(null);
		}
	};

	if (campusId === null) {
		return <React.Fragment />;
	}

	const tarjetaVivo = (c: EnVivo) => {
		const dentro = abierta === c.scheduleId;
		return (
			<div key={c.scheduleId} className="auTarjeta auTarjetaVivo">
				<button type="button" className="auPrincipal" aria-expanded={dentro} onClick={() => setAbierta(dentro ? null : c.scheduleId)}>
					<Cabeza c={c} />
					<div className="auCifras">
						<div className="auCifra"><b>{c.connected}</b> <span>de {c.enrolled} Alumnos</span></div>
						<div className="auCifra"><b>{c.withVideo}</b> <span>Cámara</span></div>
						<div className="auCifra"><b>{c.withMic}</b> <span>Micrófono</span></div>
					</div>
					<div className={"auMaestro" + (c.teacherIn ? "" : " auMaestroOro")}>{c.teacherIn ? "Maestro Adentro" : "Maestro Afuera"}</div>
				</button>
				<div className="auPie">
					<span className="auDesde">Abierta desde {c.openedLabel}</span>
					<button type="button" className="auBoton auBotonFuerte" disabled={entrando !== null} onClick={() => supervisar(c.scheduleId)}>
						{entrando === c.scheduleId ? "Entrando…" : "Entrar A Supervisar"}
					</button>
				</div>
				{errorEntrada?.id === c.scheduleId && <div className="auError">{errorEntrada.texto}</div>}
				{dentro && (
					<div className="auDetalle">
						<div className="auDetalleTitulo">Adentro</div>
						{c.people.length === 0 && <div className="auVacio">Nadie todavía.</div>}
						{c.people.map((p, i) => (
							<div className="auPersona" key={i}>
								<span className="auPersonaNombre">{p.name}</span>
								<span className="auPersonaEstado"><span>Cámara <Estado si={p.video} /></span><span>Micrófono <Estado si={p.mic} /></span></span>
							</div>
						))}
						{c.others.length > 0 && <div className="auDetalleTitulo">Otros</div>}
						{c.others.map((p, i) => (
							<div className="auPersona" key={"o" + i}>
								<span className="auPersonaNombre">{p.name}</span>
								<span className="auPersonaEstado"><span>Cámara <Estado si={p.video} /></span><span>Micrófono <Estado si={p.mic} /></span></span>
							</div>
						))}
						{c.missing.length > 0 && <div className="auDetalleTitulo">Faltan ({c.missing.length})</div>}
						{c.missing.map((n, i) => <div className="auFalta" key={"f" + i}>{n}</div>)}
					</div>
				)}
			</div>
		);
	};

	const tarjetaSinAbrir = (c: SinAbrir) => (
		<div key={c.scheduleId} className="auTarjeta auTarjetaOro auTarjetaCompacta">
			<Cabeza c={c} sinMaestro />
			<div className="auMaestro auMaestroOro">
				{!c.roomOpen ? "Aula Cerrada" : c.waiting > 0 ? `Maestro Afuera · ${c.waiting} ${c.waiting === 1 ? "Alumno Esperando" : "Alumnos Esperando"}` : "Maestro Afuera"}
			</div>
		</div>
	);

	return (
		<div className="container-fluid waModulo waAulas" style={{ padding: movil ? "10px 12px 24px 12px" : "10px 24px 32px 24px" }}>
			<div className="auCabeza">
				<h2 className="auTitulo">Aulas En Vivo</h2>
				<button type="button" className="auBoton" disabled={cargando} onClick={cargar}>{cargando ? "Actualizando…" : "Actualizar"}</button>
				{datos && <span className="auHora">Actualizado {horaDe(Date.now())}</span>}
			</div>

			{fallo && <div className="auAviso">{fallo}</div>}
			{datos && !datos.bbbOk && <div className="auAviso">No pudimos consultar el aula virtual. Se vuelve a intentar solo.</div>}

			{datos && (
				<React.Fragment>
					<section className="auBloque">
						<h3 className="auBloqueTitulo">En Vivo <span className="auCuenta">{datos.live.length}</span></h3>
						{datos.live.length === 0
							? <p className="auVacio">Ninguna clase abierta ahora.</p>
							: <div className="auRejilla">{datos.live.map(tarjetaVivo)}</div>}
					</section>

					<section className="auBloque auBloqueOro">
						<h3 className="auBloqueTitulo">Sin Abrir <span className="auCuenta">{datos.notOpened.length}</span></h3>
						{datos.notOpened.length === 0
							? <p className="auVacio">Todas las clases que ya empezaron están abiertas.</p>
							: <div className="auRejilla">{datos.notOpened.map(tarjetaSinAbrir)}</div>}
					</section>

					<section className="auBloque">
						<h3 className="auBloqueTitulo">Más Tarde Hoy <span className="auCuenta">{datos.later.length}</span></h3>
						{datos.later.length === 0
							? <p className="auVacio">No faltan clases hoy.</p>
							: (
								<div className="auCompacta">
									{datos.later.map((c) => (
										<div key={c.scheduleId} className="auTarjeta auTarjetaCompacta">
											<Cabeza c={c} sinMaestro />
										</div>
									))}
								</div>
							)}
					</section>
				</React.Fragment>
			)}
		</div>
	);
};

export default AulasEnVivoController;
